import Link from '~/(tenant)/_components/tenant-link';

import { ArrowUpRight, Check } from 'lucide-react';

import { cn } from '@kit/ui/utils';

import type { HeroBlock, HeroSlide } from '~/(tenant)/_lib/schema/landing-config.schema';
import { HeroVideo } from './hero-video';
import styles from './hero-slide.module.css';
import {
  headingFont,
  landingImageUrl,
  landingVideoUrl,
  pickLocalized,
  type LandingRenderContext,
} from './landing-context';

/**
 * One hero slide — universal (no hooks) so the static hero renders it
 * server-side and the client carousel reuses the identical markup.
 */
export function HeroSlideView({
  slide,
  ctx,
  align,
  heightClass,
  options,
  active = true,
  playbackPaused,
  onPlaybackPauseChange,
  headingLevel = 'h1',
}: {
  slide: HeroSlide;
  ctx: LandingRenderContext;
  align: 'start' | 'center';
  heightClass: string;
  options?: HeroBlock;
  active?: boolean;
  playbackPaused?: boolean;
  onPlaybackPauseChange?: (paused: boolean) => void;
  /** Carousels keep one h1 per page: the first slide is the h1, later slides are h2. */
  headingLevel?: 'h1' | 'h2';
}) {
  const Heading = headingLevel;
  const eyebrow = pickLocalized(slide.eyebrow, slide.eyebrow_ar, ctx.locale);
  const headline = pickLocalized(slide.headline, slide.headline_ar, ctx.locale);
  const emphasis = pickLocalized(slide.emphasis, slide.emphasis_ar, ctx.locale);
  const subheadline = pickLocalized(
    slide.subheadline,
    slide.subheadline_ar,
    ctx.locale,
  );
  const ctaLabel = pickLocalized(slide.ctaLabel, slide.ctaLabel_ar, ctx.locale);
  const bulletsLabel = pickLocalized(slide.bulletsLabel, slide.bulletsLabel_ar, ctx.locale);
  const secondaryCtaLabel = pickLocalized(
    slide.secondaryCtaLabel,
    slide.secondaryCtaLabel_ar,
    ctx.locale,
  );

  const centered = align === 'center';
  const therapy = options?.contentStyle === 'therapy';
  const split = options?.layout === 'split';
  const inset = options?.layout === 'inset';
  const position = options?.position ?? (centered ? 'center' : 'bottom');
  const opacity = (options?.overlayOpacity ?? 65) / 100;
  const overlay = options?.overlayDirection === 'edges'
    ? `linear-gradient(to bottom, rgb(0 0 0 / ${opacity * 0.69}) 0%, rgb(0 0 0 / ${opacity * 0.22}) 9%, transparent 20%), linear-gradient(to top, rgb(0 0 0 / ${opacity}) 0%, rgb(0 0 0 / ${opacity * 0.18}) 36%, transparent 60%)`
    : options?.overlayDirection === 'uniform'
    ? `linear-gradient(rgb(0 0 0 / ${opacity}), rgb(0 0 0 / ${opacity}))`
    : options?.overlayDirection === 'side'
      ? `linear-gradient(${ctx.locale.startsWith('ar') ? 'to left' : 'to right'}, rgb(0 0 0 / ${opacity}), rgb(0 0 0 / ${opacity * 0.15}))`
      : `linear-gradient(to top, rgb(0 0 0 / ${opacity}), rgb(0 0 0 / ${opacity * 0.45}), rgb(0 0 0 / ${opacity * 0.3}))`;
  const textColor = split ? '' : 'text-white';

  // Optional web/mobile image split + focal point for the cover crop.
  // A mobile-only image is reused at every breakpoint (better than a gradient),
  // but then there is only ONE layer — rendering both would paint the same URL
  // twice. The split flips at `sm:` to match the type scale below; using `md:`
  // gave 640-768px viewports the mobile crop with desktop-scale typography.
  const desktopKey = slide.imageKey || slide.imageKeyMobile || '';
  const mobileKey = slide.imageKeyMobile ?? '';
  const hasSplit = Boolean(slide.imageKey) && Boolean(mobileKey) && desktopKey !== mobileKey;
  const hasImage = Boolean(desktopKey);
  const hasVideo = Boolean(slide.videoKey && landingVideoUrl(slide.videoKey, ctx));
  const focalClass =
    slide.focalPoint === 'top'
      ? 'bg-top'
      : slide.focalPoint === 'bottom'
        ? 'bg-bottom'
        : slide.focalPoint === 'left'
          ? 'bg-left'
          : slide.focalPoint === 'right'
            ? 'bg-right'
            : 'bg-center';

  // Hero subheadline responds to the theme text-size tier (keeps its own
  // larger-than-body scale at each step).
  const subheadlineSize =
    ctx.textScale === 'sm'
      ? 'text-sm sm:text-base'
      : ctx.textScale === 'lg'
        ? 'text-lg sm:text-xl'
        : 'text-base sm:text-lg';

  return (
    <div
      className={cn(
        'relative flex w-full overflow-hidden',
        split ? 'flex-col md:grid md:grid-cols-2' : 'bg-neutral-950 text-white',
        inset && 'mx-auto my-6 !w-[calc(100%-3rem)] rounded-2xl sm:my-8 sm:!w-[calc(100%-4rem)]',
        heightClass,
        therapy && styles.therapy,
      )}
    >
      {hasImage || hasVideo ? (
        <>
          {/* Desktop / default image (also used everywhere when only one
              image is set, whichever one that is). */}
          <div
            className={cn(
              'overflow-hidden',
              split ? 'landing-image relative min-h-72 md:min-h-full' : 'absolute inset-0',
              split && options?.imageSide !== 'start' && 'md:order-2',
            )}
          >
            <div
              data-motion-image
              className={cn('absolute inset-0 bg-cover', focalClass, hasSplit && (therapy ? 'hidden md:block' : 'hidden sm:block'))}
              style={{
                backgroundImage: hasImage ? `url(${landingImageUrl(ctx, desktopKey)})` : undefined,
                backgroundPosition: slide.focalPoint ?? options?.imagePosition,
              }}
            />
            {hasSplit ? (
              <div
                data-motion-image
                className={cn('absolute inset-0 bg-cover', therapy ? 'md:hidden' : 'sm:hidden', focalClass)}
                style={{
                  backgroundImage: `url(${landingImageUrl(ctx, mobileKey)})`,
                  backgroundPosition: slide.focalPoint ?? options?.imagePosition,
                }}
              />
            ) : null}
            {hasVideo && slide.videoKey ? (
              <HeroVideo
                key={slide.videoKey}
                videoKey={slide.videoKey}
                ctx={ctx}
                poster={desktopKey ? landingImageUrl(ctx, desktopKey) : undefined}
                focalPoint={slide.focalPoint ?? options?.imagePosition}
                active={active}
                playbackPaused={playbackPaused}
                onPlaybackPauseChange={onPlaybackPauseChange}
              />
            ) : null}
          </div>
          {!split ? <div className={cn('pointer-events-none absolute inset-0', therapy && styles.overlay)} style={{ backgroundImage: therapy ? 'linear-gradient(137deg, var(--primary, #032E9A) 13%, #000000BF 93%)' : options?.overlayOpacity === undefined && options?.overlayDirection === undefined ? 'linear-gradient(to top, rgb(0 0 0 / .75), rgb(0 0 0 / .35), rgb(0 0 0 / .25))' : overlay }} /> : null}
        </>
      ) : (
        // No image yet — a branded gradient so the hero never looks broken.
        <>
          <div aria-hidden="true" className="absolute -start-24 -top-24 h-96 w-96 rounded-full bg-primary/35 blur-3xl" />
          <div className="absolute -bottom-32 -end-16 h-[28rem] w-[28rem] rounded-full bg-primary/25 blur-3xl" />
        </>
      )}

      <div
        className={cn(
          'landing-content landing-copy relative z-10 mx-auto flex w-full max-w-6xl flex-col gap-5 px-6 pb-16 pt-24 sm:px-8',
          options?.headlineSize === 'fluid' && 'gap-0 pb-12 lg:pb-16',
          therapy && styles.content,
          position === 'top' ? 'justify-start' : position === 'center' ? 'justify-center' : 'justify-end',
          centered && 'items-center pb-24 text-center',
          split && 'md:px-12 lg:px-16',
          split && !hasImage && !hasVideo && 'md:col-span-2',
        )}
      >
        {eyebrow ? (
          <p className={cn('text-xs font-medium uppercase tracking-[0.3em] opacity-70 sm:text-sm', options?.headlineSize === 'fluid' && 'mb-4 font-bold tracking-[0.18em] opacity-85 sm:text-xs', textColor, therapy && styles.eyebrow)}>
            {eyebrow}
          </p>
        ) : null}

        {(headline || emphasis) && (
          <Heading
            className={cn(
              options?.headlineVisibility === 'sr-only' && 'sr-only',
              'max-w-3xl whitespace-pre-line font-semibold leading-[1.05]',
              { md: 'text-4xl sm:text-5xl lg:text-6xl', lg: 'text-4xl sm:text-6xl lg:text-7xl', xl: 'text-5xl sm:text-7xl lg:text-8xl', fluid: 'mb-6 text-[clamp(2.4rem,5.5vw,5rem)] tracking-[-0.01em]' }[options?.headlineSize ?? 'lg'],
              options?.headlineSize === 'fluid' && 'max-w-2xl leading-[1.05] rtl:leading-[1.4] rtl:tracking-normal',
              textColor,
              options?.headlineStyle === 'italic' && 'italic',
              headingFont(ctx.serif),
              therapy && styles.heading,
            )}
          >
            {headline}
            {emphasis ? (
              <>
                {' '}
                <em className={cn(options?.emphasisStyle === 'accent' ? 'not-italic text-primary' : options?.emphasisStyle === 'plain' ? 'not-italic' : 'font-serif italic opacity-90')}>{emphasis}</em>
              </>
            ) : null}
          </Heading>
        )}

        {subheadline ? (
          <p
            className={cn(
              'landing-prose max-w-xl leading-relaxed opacity-80',
              subheadlineSize,
              textColor,
              therapy && styles.description,
            )}
          >
            {subheadline}
            {therapy && bulletsLabel ? <> {bulletsLabel}</> : null}
          </p>
        ) : null}

        {slide.bullets?.length ? (
          <div className={cn('max-w-xl', textColor, therapy && styles.bullets)} data-test="hero-slide-bullets">
            {bulletsLabel && (!therapy || !subheadline) ? <p className="mb-3 font-semibold">{bulletsLabel}</p> : null}
            <ul className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
              {slide.bullets.map((bullet, index) => (
                <li key={index} className="flex items-center gap-2">
                  <Check className="h-4 w-4 shrink-0" aria-hidden />
                  {pickLocalized(bullet, slide.bullets_ar?.[index], ctx.locale)}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {(ctaLabel || secondaryCtaLabel) && (
          <div
            className={cn(
              'mt-2 flex flex-wrap items-center gap-3',
              options?.headlineSize === 'fluid' && 'mt-0',
              centered && 'justify-center',
              therapy && styles.actions,
            )}
          >
            {ctaLabel ? (
              <HeroCtaLink
                href={slide.ctaHref ?? '/book'}
                className={cn('landing-button rounded-full bg-primary px-8 py-3 text-sm font-semibold tracking-wide text-primary-foreground shadow-lg transition-transform motion-safe:hover:scale-[1.03]', therapy && styles.primaryAction)}
              >
                {therapy ? <><span>{ctaLabel}</span><ArrowUpRight aria-hidden="true" size={20} /></> : ctaLabel}
              </HeroCtaLink>
            ) : null}
            {secondaryCtaLabel ? (
              <HeroCtaLink
                href={slide.secondaryCtaHref ?? '/book'}
                className={cn('landing-button rounded-full border px-8 py-3 text-sm font-semibold transition-colors hover:opacity-75', options?.secondaryCtaStyle === 'white' ? 'border-white bg-white text-primary' : split ? 'border-current' : 'border-white/40 text-white hover:bg-white/10')}
              >
                {secondaryCtaLabel}
              </HeroCtaLink>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function HeroCtaLink({
  href,
  className,
  children,
}: {
  href: string;
  className: string;
  children: React.ReactNode;
}) {
  const isExternal = /^https?:\/\//i.test(href);

  if (isExternal) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {children}
      </a>
    );
  }

  return (
    <Link href={href} className={className}>
      {children}
    </Link>
  );
}
